import * as Sentry from "@sentry/react";

export type SeverityLevel = "fatal" | "error" | "warning" | "log" | "info" | "debug";

const getConsoleByLevel = (level: SeverityLevel) => {
    switch (level) {
        case "warning":
            return console.warn;
        case "info":
        case "log":
            return console.info;
        case "debug":
            return console.debug;
        default:
            return console.error;
    }
};

const extractMessages = (data: any, fallback: string): string | Partial<Record<string, string[]>> => {
    if (!data) return fallback;
    if (typeof data === "string") return data;
    if (Array.isArray(data?.details)) return { details: data.details };
    if (typeof data?.details === "string") return data.details;
    if (typeof data?.message === "string") return data.message;
    if (typeof data === "object" && Object.keys(data).length > 0) return data;
    return fallback;
};

export const handleApiError = (
    error: any,
    fallback: string,
    feature: string = "app",
    level: SeverityLevel = "error"
): string | Partial<Record<string, string[]>> => {
    const status = error?.response?.status;
    const data = error?.response?.data;
    const url = error?.config?.url;
    const method = error?.config?.method;

    const alertMsgs = extractMessages(data, fallback);
    const plainText =
        typeof alertMsgs === "string"
            ? alertMsgs
            : Array.isArray(alertMsgs)
                ? alertMsgs.join(", ")
                : typeof alertMsgs === "object"
                    ? Object.values(alertMsgs).flat().join(", ")
                    : fallback;

    const readableText = `[${level.toUpperCase()}] ${fallback}${status ? ` — Status: ${status}` : ""
        }${url ? ` — URL: ${url}` : ""}${plainText ? ` — Details: ${plainText}` : ""}`;

    Sentry.captureMessage(readableText, {
        level,
        tags: { feature },
        extra: { fallbackMessage: fallback, status, url, method, responseData: data },
    });

    const log = getConsoleByLevel(level);
    log(`[${feature} Error]`, readableText);

    return alertMsgs;
};
